/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { AnvilorFormsAppStore } from "../anvilor-forms-app-store.service";
import { AnvilorFormsAppStoreContext } from "../anvilor-forms-app-store.service";
import { AnvilorFormsApiService } from "../../services/anvilor-forms-api.service";
import { tap } from "rxjs/operators";
import { switchMap } from "rxjs/operators";
import { EMPTY } from "rxjs";
import { Observable } from "rxjs";
import { iif } from "rxjs";
import { repeat } from "rxjs";
import { AnvilorFormSubmissionUXConfig } from "../../models/anvilor-form-config";

const DEFAULT_CLIENT_DATA_POLL_PERIOD = 1000 * 60;

interface ClientDataLoaderConfig {
  poll_period: number;
}

export class ClientDataLoader {
  private formsApi: AnvilorFormsApiService;
  private store: AnvilorFormsAppStore;

  constructor(ctx: AnvilorFormsAppStoreContext) {
    this.formsApi = ctx.formsApi;
    this.store = ctx.store;
  }

  clientDataLoadingFromSubmissionUxConfig(
    submissionUxConfig$: Observable<AnvilorFormSubmissionUXConfig>
  ): Observable<any> {
    return submissionUxConfig$.pipe(
      switchMap((config) =>
        iif(
          () => config.display_config.enable_client_data ?? false,
          this.startClientDataPolling({
            poll_period:
              config.display_config.client_data_polling_period ??
              DEFAULT_CLIENT_DATA_POLL_PERIOD,
          }),
          EMPTY
        )
      )
    );
  }

  startClientDataPolling(config: ClientDataLoaderConfig): Observable<any> {
    // QUESTION - any error handling?
    return this.loadClientData().pipe(repeat({ delay: config.poll_period }));
  }

  /**
   * We get the config from the current path. This can be called at any point to
   * load/refresh the client data.
   */
  loadClientData(): Observable<any> {
    return this.formsApi
      .getClientDataAtCurrentPath()
      .pipe(tap((config) => this.handleRetrievedClientData(config)));
  }

  private handleRetrievedClientData(client_data: any): void {
    this.store.patchState({
      client_data_load_status: "complete",
      client_data,
    });
  }
}
